import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { aboutInfo, timeline } from "@/mocks/about";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setFormStatus({ type: "success", msg: "消息已发送，我们会尽快回复您。" });
      return;
    }
    const formData = new FormData(form);
    if (honeypot) formData.delete("website_alt");

    const name = (formData.get("name") as string || "").trim();
    const email = (formData.get("email") as string || "").trim();
    const message = (formData.get("message") as string || "").trim();

    if (!name || !email || !message) {
      setFormStatus({ type: "error", msg: "请填写所有必填字段。" });
      return;
    }
    if (message.length > 500) {
      setFormStatus({ type: "error", msg: "留言内容不能超过500个字符。" });
      return;
    }

    fetch("https://readdy.ai/api/form/d9u4kdd3pcjqs2fcsm40", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        try {
          const json = JSON.parse(text);
          if (res.ok && json.code === "OK") {
            setFormStatus({ type: "success", msg: "消息已成功发送！我们会尽快回复您。" });
            form.reset();
          } else {
            const serverMsg = json?.meta?.message || json?.message || "发送失败，请稍后再试。";
            setFormStatus({ type: "error", msg: serverMsg });
          }
        } catch {
          if (res.ok) {
            setFormStatus({ type: "success", msg: "消息已成功发送！我们会尽快回复您。" });
            form.reset();
          } else {
            setFormStatus({ type: "error", msg: "发送失败，请稍后再试。" });
          }
        }
      })
      .catch(() => {
        setFormStatus({ type: "error", msg: "网络错误，请稍后再试。" });
      });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        {/* Brand Intro */}
        <section className="w-full bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
            <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
              <div className="flex-1">
                <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-4">
                  <strong>关于亚洲精品</strong>
                </h1>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {aboutInfo.introduction}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed whitespace-pre-line">
                  {aboutInfo.story}
                </p>
              </div>
              <div className="w-full lg:w-[400px] aspect-[4/3] rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20Asian%20luxury%20brand%20office%20with%20traditional%20craft%20display%20on%20warm%20cream%20background%2C%20sophisticated%20workspace%20photography%20with%20soft%20natural%20light&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="亚洲精品品牌"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
                核心价值观
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600">
                驱动我们不断前行的信念
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.values.map((v, i) => (
                <div
                  key={i}
                  className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className={`${v.icon} text-xl`} />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">
                    {v.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    {v.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
                发展历程
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600">
                从创立到成长的每一步
              </p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2" />
              <div className="space-y-8 md:space-y-10">
                {timeline.map((item, i) => (
                  <div
                    key={i}
                    className={`relative flex items-start gap-4 ${
                      i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    <div className="flex-1 md:w-1/2" />
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center z-10 relative">
                      <span className="text-xs font-bold">{item.year.slice(2)}</span>
                    </div>
                    <div className="flex-1 md:w-1/2 p-4 rounded-lg bg-background-100 border border-background-200/70">
                      <span className="text-xs font-semibold text-primary-600">{item.year}</span>
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 mt-1">
                        {item.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-600 mt-1.5 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
                品牌信誉
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600">
                业界的认可与用户的信赖
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
              {aboutInfo.reputation.stats.map((s, i) => (
                <div
                  key={i}
                  className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">
                    {s.value}
                  </div>
                  <div className="mt-1.5 text-xs md:text-sm text-foreground-600">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Media Quotes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {aboutInfo.reputation.media.map((m, i) => (
                <div
                  key={i}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <i className="ri-double-quotes-l text-2xl text-primary-300 mb-2 block" />
                  <p className="text-sm text-foreground-700 leading-relaxed italic mb-3">
                    "{m.quote}"
                  </p>
                  <div className="text-xs font-semibold text-foreground-950">—— {m.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
                联系我们
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600">
                有任何问题或合作意向，欢迎与我们联系
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
              {/* Contact Info */}
              <div className="flex-1 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">电子邮箱</div>
                    <div className="text-sm text-foreground-600">{aboutInfo.contact.email}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">客服电话</div>
                    <div className="text-sm text-foreground-600">{aboutInfo.contact.phone}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">公司地址</div>
                    <div className="text-sm text-foreground-600">{aboutInfo.contact.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">微信公众号</div>
                    <div className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">工作时间</div>
                    <div className="text-sm text-foreground-600">{aboutInfo.contact.workingHours}</div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="flex-1">
                <form
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70 space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-all resize-none"
                      placeholder="请输入您的留言（不超过500字符）"
                    />
                    <div className="text-xs text-foreground-400 mt-1">不超过500个字符</div>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  {formStatus && (
                    <div
                      className={`text-sm p-3 rounded-md ${
                        formStatus.type === "success"
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-red-50 text-red-700 border border-red-200"
                      }`}
                    >
                      {formStatus.msg}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="w-full px-4 py-2.5 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer border-none"
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}